<template>
  <section class="home home--portfolio" id="HeaderWhite" ref="portfolioSection">
    <div class="home__bg" ref="portfolioBG"></div>
    <div class="container position-relative">
      <div class="row">
        <div class="col-12">
          <div class="home__title" 
               :style="{ transform: isMdOrLarger ? `translateY(${titleOffset}px)` : 'translateY(0)' }">
            <div class="home__text" v-html="translate('our.portfolio.html')"></div>
          </div>
          <div class="home__wrapper">
            <ShortcodesPortfolioHome />
          </div>
          <div class="home__btn" ref="shinyButton">
            <NuxtLink class="btn btn--primary-white" :to="localePath('/our-portfolio')" target="_self">
              <span class="btn__wrapper">
                <span class="btn__title"> {{ translate('explore.more') }}</span>
                <span class="btn__icon"><i class="icon-arrow-button"></i></span>
              </span>
              <span class="btn__tooltip text-uppercase">{{ translate('our.portfolio') }}</span>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>


<script setup>
import { useTranslationStore } from '@/stores/translations'
import { useShinyButton } from '@/composables/useShinyButton'
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

// ดึง store สำหรับการแปล
const translationStore = useTranslationStore()

// ดึงข้อมูลการแปลเมื่อ component ถูก mount
const fetchTranslations = async () => {
  await translationStore.fetchTranslations();
}

// สร้างฟังก์ชันสำหรับการแปล
const translate = translationStore.translate
// ใช้ shinyButton
const { shinyButton } = useShinyButton()


//#### Start scroll ######
gsap.registerPlugin(ScrollTrigger)

const portfolioSection = ref(null)
const titleOffset = ref(0)
let portfolioScrollTrigger = null 
const isMdOrLarger = ref(false); 
let portfolioBGScrollTrigger = null


const checkScreenSize = () => {
  isMdOrLarger.value = window.innerWidth >= 768;
};

const initScrollTrigger = async () => {
  if (portfolioScrollTrigger) {
    portfolioScrollTrigger.kill();
  }


  let endValue;
  if (window.innerWidth <= 768) { 
    endValue = "bottom-=600 top"; 
  } else if (window.innerWidth <= 1024) { 
    endValue = "bottom-=800 top"; 
  } else { 
    endValue = "bottom-=800 top";
  }


  portfolioScrollTrigger = ScrollTrigger.create({
    trigger: portfolioSection.value,
    start: "top top",
    end: endValue,
    markers: false,
    onUpdate: (self) => {
      if (portfolioSection.value && isMdOrLarger.value) { 
        const section = portfolioSection.value;
        const sectionRect = section.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        if (sectionRect.top <= windowHeight && sectionRect.bottom >= 0) {
          const scrollStart = section.offsetTop;
          const currentScroll = window.scrollY - scrollStart;
          
          titleOffset.value = Math.max(0, currentScroll);
        } else {
          titleOffset.value = 0;
        }
      } else {
        titleOffset.value = 0; 
      }
    },
  });

  if (portfolioBGScrollTrigger) {
    portfolioBGScrollTrigger.kill(); 
  }

  // ตรวจสอบว่า portfolioSection มีอยู่ใน DOM หรือไม่
  if (portfolioSection.value) {
    portfolioBGScrollTrigger = ScrollTrigger.create({
      trigger: portfolioSection.value,
      start: "top top", 
      end: "bottom top", 
      markers: false, 
      onEnter: () => {
        document.body.classList.add('is-black');
      },
      onLeave: () => {
        document.body.classList.remove('is-black');
      },
      onEnterBack: () => {
        document.body.classList.add('is-black');
      },
      onLeaveBack: () => {
        document.body.classList.remove('is-black');
      }
    });
  }

  const cards = document.querySelectorAll('.card--portfolio'); 
  cards.forEach((card, index) => {
    gsap.fromTo(card, 
      { y: 60, opacity: 1 }, 
      {
        y: 0, 
        opacity: 1,
        scrollTrigger: {
          trigger: card,
          start: "top center", 
          end: "bottom top", 
          scrub: true, 
          markers: false 
        }
      }
    );
  });
}

const observeOverview = () => {
  const overviewSection = document.querySelector('.home--overview');
  if (!overviewSection) return null;

  const observer = new MutationObserver((mutations) => {
    nextTick(() => {
      initScrollTrigger();
      ScrollTrigger.refresh(true);
    });
  });


  observer.observe(overviewSection, {
    attributes: true,
    childList: true,
    subtree: true,
    characterData: true
  });

  return observer;
}

onMounted(async () => {
  let observer;

  checkScreenSize();

  await fetchTranslations();
  nextTick(() => {
    observer = observeOverview();
    initScrollTrigger(); // initScrollTrigger after data finished
    ScrollTrigger.refresh(true);
  });

  // event listener resize
  window.addEventListener('resize', () => {
    checkScreenSize(); 
    initScrollTrigger();
    ScrollTrigger.refresh(true);
  });
});

// Cleanup
onUnmounted(() => {
  if (portfolioScrollTrigger) {
    portfolioScrollTrigger.kill();
  }
  

  if (portfolioBGScrollTrigger) {
    portfolioBGScrollTrigger.kill(); // ทำลาย ScrollTrigger เมื่อ component ถูก unmounted
  }
  
  // ล้าง event listener
  window.removeEventListener('resize', () => {
    checkScreenSize();
    initScrollTrigger();
    ScrollTrigger.refresh(true);
  });

  document.body.classList.remove('is-black');
});

//#### End scroll ######

</script>

<style lang="scss">
@import "@/assets/scss/app";

@each $lang in $languages {
  html[lang="#{$lang}"] {
    .home {
      &.home--portfolio {
        .home__text {
          @include font-size("h2", $lang);
          @include font-weight("semibold", $lang);
          line-height: 1;
        }

        .home__highlight {
          @include font-family('highlight', $lang);
          @include font-weight("medium", $lang);
          line-height: 1.3;
        }
      }
    }
  }
}


.home {
  &.home--portfolio {
    position: relative;
    overflow: hidden;
    padding-bottom: 5rem;
    color: $white;

    @include media-breakpoint-down(lg) {
      padding-bottom: 0rem;
    }

    .home__title {
      position: absolute;
      top: 6%;
      left: 50%;
      transform: translateX(-50%);
      z-index: 3;
      mix-blend-mode: difference;
      width: 50%;
      pointer-events: none;

      @include media-breakpoint-down(md) {
        top: 10%;
        left: 0;
        width: 100%;
        text-align: center;
      }
      @include media-breakpoint-down(sm) {
        position: relative;
        top: 0;
        padding: 3rem 0 1rem;
      }
    }

    .home__text {
      color: $white;
      display: inline-block;
    }

    .home__wrapper {
      position: relative;
      z-index: 1;
    }

    .home__highlight {
      font-style: italic;
    }

    .home__wrapper {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 8%;

      @include media-breakpoint-down(md) {
        gap: 2%;
      }

      @include media-breakpoint-down(sm) {
        gap: 0%;
      }

      .card {
        &.card--portfolio {
          &:nth-child(1) {
            margin-top: 5rem;
            max-width: 55%;
            flex: 0 0 55%;

            @include media-breakpoint-down(md) {
              margin-top: 3rem;
              max-width: 48%;
              flex: 0 0 48%;
            }

            @include media-breakpoint-down(sm) {
              margin-top: 2rem;
              max-width: 100%;
              flex: 0 0 100%;
            }
          }

          &:nth-child(2) {
            margin-top: 14rem;
            max-width: 37%;
            flex: 0 0 37%;

            @include media-breakpoint-up(lg) {
              .card__image {
                min-height: 400px;

                img {
                  min-height: 400px;
                }
              }
            }

            @include media-breakpoint-down(md) {
              margin-top: 3rem;
              max-width: 48%;
              flex: 0 0 48%;
            }

            @include media-breakpoint-down(sm) {
              margin-top: 2rem;
              max-width: 100%;
              flex: 0 0 100%;
            }
          }

          &:nth-child(3) {
            margin-top: 10rem;
            max-width: 37%;
            flex: 0 0 37%;

            @include media-breakpoint-up(lg) {
              .card__image {
                min-height: 400px;

                img {
                  min-height: 400px;
                }
              }
            }

            @include media-breakpoint-down(md) {
              margin-top: 3rem;
              max-width: 48%;
              flex: 0 0 48%;
            }

            @include media-breakpoint-down(sm) {
              margin-top: 2rem;
              max-width: 100%;
              flex: 0 0 100%;
            }
          }

          &:nth-child(4) {
            margin-top: 5rem;
            max-width: 55%;
            flex: 0 0 55%;

            @include media-breakpoint-down(md) {
              margin-top: 3rem;
              max-width: 48%;
              flex: 0 0 48%;
            }

            @include media-breakpoint-down(sm) {
              margin-top: 2rem;
              max-width: 100%;
              flex: 0 0 100%;
            }
          }

          &:nth-child(5) {
            margin-top: 6rem;
            max-width: 55%;
            flex: 0 0 55%;

            @include media-breakpoint-down(md) {
              margin-top: 3rem;
              max-width: 48%;
              flex: 0 0 48%;
            }

            @include media-breakpoint-down(sm) {
              margin-top: 2rem;
              max-width: 100%;
              flex: 0 0 100%;
            }
          }
        }
      }
    }

    .home__btn {
      margin-top: 2.75rem;
      text-align: center;
    }

  }
}
</style>
